import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const fields = [
  { key: "store_name", label: "Store name", type: "text" },
  { key: "tagline", label: "Tagline", type: "text" },
  { key: "phone", label: "Phone number", type: "text" },
  { key: "whatsapp", label: "WhatsApp number (digits, incl. country code)", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "address", label: "Address", type: "text" },
  { key: "hours", label: "Opening hours", type: "text" },
  { key: "order_email", label: "Order form destination email", type: "text" },
  { key: "about", label: "About text", type: "textarea" },
];

export default function AdminSettings() {
  const { reloadSettings } = useStore();
  const { user: me } = useAuth();
  const canEdit = me?.role === "super_admin" || me?.role === "manager";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data } = useQuery({
    queryKey: ["settings-admin"],
    queryFn: () => api.get("/settings").then((r) => r.data),
  });

  useEffect(() => {
    if (data) setForm({
      store_name: data.store_name || "", tagline: data.tagline || "", phone: data.phone || "",
      whatsapp: data.whatsapp || "", email: data.email || "", address: data.address || "",
      hours: data.hours || "", about: data.about || "", store_enabled: !!data.store_enabled,
      maintenance_mode: !!data.maintenance_mode, order_email: data.order_email || "",
    });
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
      reloadSettings();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!form) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;

  if (!canEdit) {
    return (
      <div data-testid="admin-settings-denied" className="max-w-lg mx-auto mt-16 text-center bg-white border rounded-xl p-10">
        <h1 className="font-display font-bold text-2xl text-slate-900">Restricted area</h1>
        <p className="text-slate-500 mt-2">Only a Super Admin or Manager can edit store settings.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings" className="max-w-2xl">
      <h1 className="font-display font-black text-3xl text-slate-900 mb-1">Store settings</h1>
      <p className="text-slate-500 mb-8">Contact details and content shown across your website.</p>

      <div className="bg-white border rounded-xl p-6 space-y-5">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-semibold mb-1">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea data-testid={`settings-${f.key}`} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={4} className="w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            ) : (
              <input data-testid={`settings-${f.key}`} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            )}
          </div>
        ))}

        <div className={`flex items-start gap-3 p-4 border-2 rounded-lg ${form.maintenance_mode ? "bg-amber-50 border-amber-400" : "bg-slate-50 border-slate-200"}`}>
          <input data-testid="settings-maintenance-mode" type="checkbox" checked={form.maintenance_mode} onChange={(e) => setForm({ ...form, maintenance_mode: e.target.checked })} className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold text-slate-800">Maintenance mode {form.maintenance_mode && <span className="ml-1 text-xs font-bold text-amber-700 bg-amber-200 px-2 py-0.5 rounded-full">ON</span>}</p>
            <p className="text-sm text-slate-500">When on, visitors see a "Under maintenance" page while you work. The admin panel stays open, and signed-in admins can still preview the live website.</p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-slate-50 border rounded-lg">
          <input data-testid="settings-store-enabled" type="checkbox" checked={form.store_enabled} onChange={(e) => setForm({ ...form, store_enabled: e.target.checked })} className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold text-slate-800">Enable online web store</p>
            <p className="text-sm text-slate-500">When ready, switch this on to let customers checkout online via Paystack. (Checkout UI is built but hidden while off.)</p>
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-6 py-3 rounded-lg hover:bg-brand-blue/90 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save settings
        </button>
      </div>
    </div>
  );
}
